/**
 * Responsible for applying validation upon a radio group so that it can hook
 * into Constraint Validation API.
 * @module radioGroupValidator
 */
import $ from 'jquery';
import find from 'lodash/find';
import property from 'lodash/property';
import uniq from 'lodash/uniq';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';
import { EVENTS } from './validator';
import stache from '../../lib/stache';
import random from '../../lib/random';

const INPUT_GROUP_OPTIONS = {
  errorClassName: '',
  inputSelector: 'input',
  errorPosition: 'afterbegin',
  labelSelector: '.push-button',
  labelErrorClassName: 'push-button-error',
  errorTemplate: '',
  errorIdentifiedBy: '',
  errorPrefix: 'Error:',
};

const createInputGroupOptions = () => ({
  ...INPUT_GROUP_OPTIONS,
  errorIdentifiedBy: random({
    prefix: 'radio-group-validator-error',
  }),
});

/**
 * CSS class name used for embedding an error.
 * @type {string}
 */
export const EMBEDDED_ERROR_CLASS_NAME = 'embedded-error';

/**
 * Default error message when one hasn't been provided.
 * @type {string}
 */
export const DEFAULT_ERROR_MESSAGE = 'Please select an option';

/**
 * Responsible for taking any registered event listeners and releasing them
 * from memory.
 * @private
 * @param {HTMLInputElement} input - A radio input
 * @param {Array<Object>} listeners - event listeners to be removed
 */
const removeInputListeners = (input, listeners) => {
  listeners.forEach(({ evt, handler }) => {
    input.removeEventListener(evt, handler);
  });
};

/**
 * Responsible for removing the error message.
 * @private
 * @param {HTMLElement} wrap - Container element
 */
const removeError = (wrap) => {
  const potentialErrors = [
    () => wrap.querySelector(`.${EMBEDDED_ERROR_CLASS_NAME}`),
    () => {
      const el = wrap.previousSibling;
      return el && el.classList.contains(EMBEDDED_ERROR_CLASS_NAME) && el;
    },
    () => {
      const el = wrap.nextSibling;
      return el && el.classList.contains(EMBEDDED_ERROR_CLASS_NAME) && el;
    },
  ];

  const errorElFn = potentialErrors.find(fn => fn());
  if (errorElFn) {
    const errorEl = errorElFn();
    errorEl.parentNode.removeChild(errorEl);
    return;
  }

  throw new Error('No error element found in radio group validator');
};

/**
 * Responsible for building the element for the error.
 * @private
 * @param {string} errorClassName - CSS class name for the error
 * @param {string} errorMessage - The error message
 */
const renderError = ({ errorClassName, errorMessage, errorPrefix, id }) => {
  const errorElement =
    `<p ${id ? `id="${id}"` : ''} class="${errorClassName} ${EMBEDDED_ERROR_CLASS_NAME}"><span class="u-screen-reader">${errorPrefix} </span>${errorMessage}</p>`;

    return errorElement;
}


/**
 * Responsible for figuring out where the error message should be rendered in
 * the DOM.
 * @private
 * @param {Object} dataset - dataset of an HTML
 * @returns {Object} The position and selector used for inserting the error
 */
const determineInsertSelector = (dataset) => {
  const {
    radioGroupInsertErrorBefore: insertBeforeSelector,
    radioGroupInsertErrorAfter: insertAfterSelector,
  } = dataset;

  const selectors = [
    {
      position: 'beforebegin',
      insertSelector: insertBeforeSelector,
    },
    {
      position: 'afterend',
      insertSelector: insertAfterSelector,
    },
  ];

  return find(selectors, ({ position, insertSelector }) => {
    if (insertSelector) {
      return { position, insertSelector };
    }

    return false;
  }) || {};
};

/**
 * Responsible for retrieving an error message based on a number of rules from
 * most specific to least specific, in particular:
 *
 * - error message on the element
 * - template on an element
 * - template based on passed in options
 * - default error message as a catch all
 *
 * @param {string} errorMessage - Element error message
 * @param {string} template - Stache template for the error message
 * @param {Object} data - Data to be fed into stache template
 * @returns {string} An error message
 */
const getErrorMessage = (errorMessage, template, data) => {
  if (errorMessage) {
    return errorMessage;
  }

  if (template) {
    return stache(template, data);
  }

  return DEFAULT_ERROR_MESSAGE;
};

/**
 * Finds the first input that contains a data-label.
 * @param {Array} inputs - List of input elements in the form of an array
 * @returns {string} First found label attribute
 */
const findLabelFromInputs = (inputs) => {
  const getLabel = property('dataset.label');
  const input = find(inputs, getLabel);
  if (input) {
    return getLabel(input);
  }

  return '';
};

/**
 * Responsible for appending a new value to aria-labelledby that will be used
 * to communicate an error.
 * @param {HTMLElement} container - Element containing the radio group validator
 * @param {string} errorIdentifiedBy - Value used for aria-labelledby
 */
const applyLabelledByError = (container, errorIdentifiedBy) => {
  const existingLabelledBy = (container.getAttribute('aria-labelledby') || '')
    .split(/(\s+)/)
    .filter(val => val.trim().length);
  const updatedLabelledBy = uniq([...existingLabelledBy, errorIdentifiedBy])
    .join(' ')
    .trim();
  container.setAttribute('aria-labelledby', updatedLabelledBy);
};

/**
 * Responsible for marking a set of inputs as being aria valid or invalid.
 * @param {HTMLInputElement[]} inputs - Elements made valid or invalid
 * @param {boolean} val - Determines whether the elements should be invalid
 */
const applyAriaInvalid = (inputs, val) => {
  inputs.forEach((input) => {
    input.setAttribute('aria-invalid', val);
  });
};

/**
 * Responsible for seeing if any radio inputs are invalid and if so, to allow
 * for error messaging to be displayed. Once a change event has occurred, the
 * error goes away and will allow for any additional validation to be picked up
 * and allow the cycle to repeat itself.
 *
 * The error message and where it's located is configurable based on data
 * attributes on the element passed in. By default, if no position has been
 * provided, it'll write the error message as the first child of the wrap.
 * Here are the supported position values:
 *
 * - data-radio-group-insert-error-before
 * - data-radio-group-insert-error-after
 *
 * Where the value is a selector within the element passed in. Here's an
 * example:
 *
 * @example
 * <div data-radio-group-error-message="Yeah,well, that's just, like, your opinion, man"
 *      data-radio-group-insert-error-before=".title">
 *   <h2 class="title">I am a title or something</h2>
 *   <input type="radio" name="characters" value="dude" required>
 *   <input type="radio" name="characters" value="walter" required>
 *   <input type="radio" name="characters" value="donny" required>
 *   <button type="submit">Submit</button>
 * </div>
 *
 * @see INPUT_GROUP_OPTIONS
 * @param {HTMLElement} wrap - Element containing the inputs
 * @param {Object} options - Configurable options
 */
export default function radioGroupValidator(wrap, options = {}, { deregister } = noopActions) {
  const { dataset } = wrap;
  const {
    inputSelector,
    errorClassName,
    errorPosition,
    labelSelector,
    labelErrorClassName,
    radioGroupErrorMessage,
    errorTemplate,
    errorIdentifiedBy,
    errorPrefix,
  } = Object.assign({}, createInputGroupOptions(), options, dataset);
  const { position = errorPosition, insertSelector } = determineInsertSelector(dataset);
  const insertionEl = insertSelector ? wrap.querySelector(insertSelector) : wrap;
  const { INVALID_SCROLL_POSITION } = EVENTS;

  let errorShown = false;
  let listeners = [];

  const inputs = Array.from(wrap.querySelectorAll(inputSelector));
  const $labels = $(wrap).find(labelSelector);
  const label = findLabelFromInputs(inputs);
  const errorMessage = getErrorMessage(radioGroupErrorMessage, errorTemplate, { label });
  applyLabelledByError(wrap, errorIdentifiedBy);
  inputs.forEach((input) => {
    let invalidHandler;
    let changeHandler;
    let positionHandler;

    input.addEventListener('invalid', invalidHandler = () => {
      if (!errorShown) {
        errorShown = true;
        insertionEl.insertAdjacentHTML(position, renderError({
          errorClassName,
          errorMessage,
          errorPrefix,
          id: errorIdentifiedBy,
        }));
        $labels.addClass(labelErrorClassName);
        inputs[0].setCustomValidity(errorMessage);
        applyAriaInvalid(inputs, true);
      }
    });

    input.addEventListener(
      INVALID_SCROLL_POSITION,
      positionHandler = ({ detail: { setElement } }) => {
        setElement(wrap);
      },
    );

    input.addEventListener('change', changeHandler = () => {
      if (errorShown) {
        errorShown = false;
        removeError(wrap);
        removeInputListeners(input, listeners);
        $labels.removeClass(labelErrorClassName);
        inputs[0].setCustomValidity('');
        applyAriaInvalid(inputs, false);
      }
    });

    listeners = [...listeners,
      { evt: 'invalid', handler: invalidHandler },
      { evt: 'change', handler: changeHandler },
      { evt: INVALID_SCROLL_POSITION, handler: positionHandler },
    ];
  });

  return {
    removeListeners: () => {
      deregister();
      inputs.forEach((input) => {
        listeners.forEach(({ evt, handler }) => {
          input.removeEventListener(evt, handler);
        });
      });
    },
  };
}

registerJQueryPlugin('radioGroupValidator', radioGroupValidator);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/radio-group-validator.js